"use client";

import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@/components/ui/button";
import { ServiceCard } from "./ServiceCard";
import type { PageSection } from "@/types";

interface ServicesCarouselProps {
  section: PageSection | undefined;
}

export function ServicesCarousel({ section }: ServicesCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  if (!section || !section.items || section.items.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        {section.title && (
          <h2 className="mb-2 text-center text-3xl font-bold md:text-4xl">
            {section.title}
          </h2>
        )}
        {section.subtitle && (
          <p className="mx-auto mb-10 max-w-2xl text-center text-muted-foreground">
            {section.subtitle}
          </p>
        )}

        <div className="relative px-12">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex -ml-4">
              {section.items.map((item, i) => (
                <div
                  key={i}
                  className="flex-[0_0_100%] min-w-0 pl-4 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
                >
                  <ServiceCard item={item} />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-0 top-1/2 z-40 -translate-y-1/2 active:!-translate-y-1/2 rounded-full hidden md:flex"
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4 pointer-events-none" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute right-0 top-1/2 z-40 -translate-y-1/2 active:!-translate-y-1/2 rounded-full hidden md:flex"
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 pointer-events-none" />
          </Button>
        </div>
      </div>
    </section>
  );
}
